import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaDownload, FaFilter, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { graphqlRequest } from '../../utils/graphqlRequest';
import { SUPPORT_TICKETS_QUERY } from '../../graphql/supportTicket';

function ticketIdLabel(t) {
  const n = Number(t.ticketNumber);
  if (!Number.isFinite(n) || n <= 0) return t.id;
  return `TKT-${String(n).padStart(3, '0')}`;
}

function normalizeRole(r) {
  const s = String(r || '').toLowerCase();
  if (s.includes('admin')) return 'admin';
  if (s.includes('field cleaner') || s.includes('fieldcleaner')) return 'cleaner';
  if (s.includes('cleaner')) return 'cleaner';
  if (s.includes('contractor')) return 'contractor';
  if (s.includes('manager') || s.includes('supervisor')) return 'manager';
  return 'manager';
}

function roleBadgeClass(role) {
  if (role === 'admin') return 'smgmt-badge smgmt-badge--violet';
  if (role === 'cleaner') return 'smgmt-badge smgmt-badge--cleaner';
  if (role === 'contractor') return 'smgmt-badge smgmt-badge--contractor';
  return 'smgmt-badge smgmt-badge--manager';
}

function roleLabel(role) {
  if (role === 'admin') return 'Admin';
  if (role === 'cleaner') return 'Cleaner';
  if (role === 'contractor') return 'Contractor';
  return 'Manager';
}

function priorityBadgeClass(p) {
  const map = {
    urgent: 'smgmt-badge smgmt-badge--urgent',
    high: 'smgmt-badge smgmt-badge--high',
    medium: 'smgmt-badge smgmt-badge--medium',
    low: 'smgmt-badge smgmt-badge--low',
  };
  return map[p] || map.medium;
}

function priorityLabel(p) {
  return p.charAt(0).toUpperCase() + p.slice(1);
}

function statusBadgeClass(status) {
  const map = {
    open: 'smgmt-badge smgmt-badge--open',
    progress: 'smgmt-badge smgmt-badge--progress',
    resolved: 'smgmt-badge smgmt-badge--resolved',
    escalated: 'smgmt-badge smgmt-badge--urgent',
  };
  return map[status] || map.open;
}

function statusLabel(status) {
  if (status === 'progress') return 'In Progress';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatCreatedDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toISOString().slice(0, 10);
  } catch {
    return '—';
  }
}

function normalizePriority(p) {
  const v = String(p || '').toUpperCase();
  if (v === 'URGENT') return 'urgent';
  if (v === 'HIGH') return 'high';
  if (v === 'NORMAL') return 'medium'; // UI calls NORMAL "Medium"
  if (v === 'LOW') return 'low';
  return String(p || '').toLowerCase();
}

function normalizeStatus(s) {
  const v = String(s || '').toUpperCase();
  if (v === 'OPEN') return 'open';
  if (v === 'IN_PROGRESS') return 'progress';
  if (v === 'RESOLVED') return 'resolved';
  return String(s || '').toLowerCase();
}

export default function SupportTicketManagement() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await graphqlRequest(SUPPORT_TICKETS_QUERY);
      setTickets(data?.supportTickets || []);
    } catch (e) {
      setLoadError(e?.message || 'Failed to load tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const rows = useMemo(() => {
    return tickets
      .map((t) => ({
        ...t,
        displayId: ticketIdLabel(t),
        submittedBy: t.submittedByName || t.submittedByEmail || '—',
        role: normalizeRole(t.submittedByRole),
        project: t.project || '—',
        priority: normalizePriority(t.priority),
        status: normalizeStatus(t.status),
        assignedAdmin: t.assignedAdmin || '—',
        created: formatCreatedDate(t.createdAt),
      }))
      .filter((t) => {
        if (roleFilter !== 'all' && t.role !== roleFilter) return false;
        if (priorityFilter !== 'all' && String(t.priority || '') !== priorityFilter)
          return false;
        if (statusFilter !== 'all' && String(t.status || '') !== statusFilter)
          return false;
      if (query.trim()) {
        const q = query.toLowerCase();
        const hay = `${t.displayId} ${t.subject} ${t.submittedBy} ${t.project}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, roleFilter, priorityFilter, statusFilter, query]);

  const total = tickets.length;

  const exportCsv = useCallback(() => {
    const header = [
      'Ticket ID',
      'Subject',
      'Submitted By',
      'Role',
      'Project',
      'Category',
      'Priority',
      'Status',
      'Assigned Admin',
      'Created',
    ];

    const lines = [
      header.join(','),
      ...rows.map((t) =>
        [
          t.displayId,
          t.subject,
          t.submittedBy,
          roleLabel(t.role),
          t.project,
          t.category,
          priorityLabel(t.priority),
          statusLabel(t.status),
          t.assignedAdmin,
          t.created,
        ]
          .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
          .join(','),
      ),
    ].join('\n');

    const blob = new Blob([lines], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `support-tickets-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  return (
    <div className="support-tickets-page">
      <header className="support-tickets-page__head">
        <h1>Ticket Management</h1>
        <p>Manage and track all support tickets</p>
        {loadError ? (
          <p style={{ color: '#b91c1c', marginTop: 8 }}>{loadError}</p>
        ) : null}
      </header>

      <section className="support-tickets-page__card support-tickets-page__card--filters">
        <h2 className="support-tickets-page__filters-title">
          <FaFilter aria-hidden className="support-tickets-page__filters-icon" />
          Filters
        </h2>
        <div className="support-tickets-page__filters-grid">
          <label className="support-tickets-page__field">
            <span className="support-tickets-page__field-label">Search</span>
            <div className="support-tickets-page__search-wrap">
              <FaSearch className="support-tickets-page__search-icon" aria-hidden />
              <input
                type="search"
                className="support-tickets-page__search-input"
                placeholder="Search tickets..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
              />
            </div>
          </label>
          <label className="support-tickets-page__field">
            <span className="support-tickets-page__field-label">User role</span>
            <select
              className="support-tickets-page__select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">All roles</option>
              <option value="cleaner">Cleaner</option>
              <option value="contractor">Contractor</option>
              <option value="manager">Manager</option>
            </select>
          </label>
          <label className="support-tickets-page__field">
            <span className="support-tickets-page__field-label">Priority</span>
            <select
              className="support-tickets-page__select"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="all">All priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </label>
          <label className="support-tickets-page__field">
            <span className="support-tickets-page__field-label">Status</span>
            <select
              className="support-tickets-page__select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="open">Open</option>
              <option value="progress">In progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </label>
        </div>
      </section>

      <section className="support-tickets-page__card support-tickets-page__card--table">
        <div className="support-tickets-page__toolbar">
          <div>
            <h2 className="support-tickets-page__table-title">All Tickets</h2>
            <p className="support-tickets-page__table-meta">
              {loading ? 'Loading…' : `Showing ${rows.length} of ${total} tickets`}
            </p>
          </div>
          <button type="button" className="support-tickets-page__export" onClick={exportCsv}>
            <FaDownload aria-hidden />
            Export
          </button>
        </div>
        <div className="support-tickets-page__table-wrap">
          <table className="support-tickets-page__table">
            <thead>
              <tr>
                <th>Ticket ID</th>
                <th>Subject</th>
                <th>Submitted by</th>
                <th>Role</th>
                <th>Project</th>
                <th>Category</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Assigned admin</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="support-tickets-page__table-meta">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="support-tickets-page__table-meta">
                    No tickets found.
                  </td>
                </tr>
              ) : null}
              {rows.map((t) => (
                <tr key={t.id}>
                  <td>
                    <button type="button" className="support-ticket-id">
                      {t.displayId}
                    </button>
                  </td>
                  <td>{t.subject}</td>
                  <td>{t.submittedBy}</td>
                  <td>
                    <span className={roleBadgeClass(t.role)}>{roleLabel(t.role)}</span>
                  </td>
                  <td>{t.project}</td>
                  <td>{t.category}</td>
                  <td>
                    <span className={priorityBadgeClass(t.priority)}>{priorityLabel(t.priority)}</span>
                  </td>
                  <td>
                    <span className={statusBadgeClass(t.status)}>{statusLabel(t.status)}</span>
                  </td>
                  <td>{t.assignedAdmin}</td>
                  <td>{t.created}</td>
                  <td>
                    <button
                      type="button"
                      className="smgmt-btn smgmt-btn-purple"
                      disabled={t.status === 'resolved'}
                      onClick={() => navigate(`/support-management/tickets/${t.id}/resolve`)}
                    >
                      Resolve
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
